import answers from "../reducer/answers";
import { combineReducers } from "redux";
import metadata from "./responseMetadata";
import { createSelector } from "reselect";
import { nextQuestionForSection } from "../reducer";
import userEdits from "../reducer/userEdits";
import {
  DEFAULT_ORDER,
  CUSTOMIZED_ORDER,
  ORDER_BY_POSITIVE_ATTRIBUTES,
  ORDER_BY_CATEGORY,
  ORDER_BY_NAME,
  ORDER_BY_ASSESSMENT_TIER
} from "../actions/filterOptions";
import {assessmentTiers} from "../components/resultItems/AssessmentTier";
import {isEmpty} from 'lodash';

export default combineReducers({
  userEdits,
  metadata,
  answers
});

const getQuestions = state => state.questions;
const getOptions = state => state.options;
const getCategories = state => state.categories;

class QuestionCompletionState {
  constructor() {
    this.completionStates = {
      monitoring: true,
      assessment: true,
      controlRules: true,
      enforcement:true
    };

    this.incompleteQuestions = {
      monitoring: {},
      assessment: {},
      controlRules: {},
      enforcement: {}
    };

    this.completedQuestions = {
      monitoring: {},
      assessment: {},
      controlRules: {},
      enforcement: {}
    };
  }

  markIncomplete(section, questionID) {
    if (!questionID) {
      throw new Error("Question ID not specified");
    } else {
      this.completionStates[section] = false;
      this.incompleteQuestions[section][questionID] = true;
    }
  }

  markCompleted(section, questionID) {
    this.completedQuestions[section][questionID] = true;
  }

  isComplete(section) {
    return this.completionStates[section];
  }

  numIncompleteQuestions(section) {
    return Object.keys(this.incompleteQuestions[section]).length;
  }

  numCompletedQuestions(section) {
    return Object.keys(this.completedQuestions[section]).length;
  }
}

const influence = caveat => {
  switch (caveat.score) {
    case -3:
      return 100000;
    case -2:
      return 1000;
    case -1:
      return 10;
    default:
      return 0;
  }
};

export const criteriaColors = [
  "rgb(250,20,27)", //red
  "rgb(252,164,41)", // orange
  "rgb(231,214,91)", // yellow
	"rgb(50,205,50)" //limegreen
];

export const getCriteriaColor = (answer, answers, criteria, hideColorCoding) => {
  const answerValues = answers.map((a) => a.id);
  let passingIndex = -1;
  for (var i = 0; i < answers.length; i++) {
    if (criteria.indexOf(answers[i].id) !== -1) {
      // first passing criteria
      passingIndex = i;
      break;
    }
  }
  // CB 2/5/19
  // Right now we expect all criteria to be selected in the admin interface. This may be redundant
  // but I don't want to change this behavior without being sure it won't cause problems. So, I'm
  // setting all non-passing criteria as black here for now. See:
  // https://github.com/shcaba/FishPath-updates/issues/183
  // Note that this doesn't effect anything in the assessment section (w/ option hideColorCoding),
  // since there we are using multiple colors so it really isn't assumed that all criteria must be
  // chosen in admin
  let passes = true;
  if (criteria.indexOf(answer) === -1) {
    passes = false;
  }

  let colors = ["rgb(0,0,0)"] //default to black for when no answer meets criteria (needed for multi)
  if(passingIndex >= 0){ //if an answer matches criteria, update the color array
    const passingAnswers = answers.slice(passingIndex);
    colors = [criteriaColors[0], ...criteriaColors.slice(1).slice(4 - passingAnswers.length)]
  }

  const answerIndex = answerValues.indexOf(answer);
  if (hideColorCoding) {
    if (answerIndex < passingIndex || !passes) {
      return "rgb(0,0,0)";
    } else {
      return criteriaColors[criteriaColors.length - 1];
    }
  } else {
    if (answerIndex < passingIndex) {
      return "rgb(0,0,0)";
    } else if (answers.length === 2) {
      return criteriaColors[3]; // green, yes or no
    } else if (answerIndex === passingIndex){
      return criteriaColors[0]; // red
    } else {
      // return [criteriaColors[0], ...criteriaColors.slice(1).slice(4 - answers.length)][Math.min(answerIndex - passingIndex, criteriaColors.length - 1)];
      return colors[Math.min(answerIndex - passingIndex, colors.length - 1)];
    }
  }
}

//return an array of all the sections that a question appears in (ie will trigger caveats or criteria for)
export const getQuestionSections = (question, options) => {
  var sections = [];
  options = !options ?  global.store.getState().options : options
  var domainLogic = question.domainLogic;
  if (domainLogic.criteria && Object.keys(domainLogic.criteria).length > 0) {
    for (var id in domainLogic.criteria) {
      const option = options.find((o) => parseInt(o.id) === parseInt(id));
      //currently when criteria are deleted, the option id is still listed, so need to check that it is not null
      if (option && !!domainLogic.criteria[id]) {
        if(!sections.includes(option.section)){
          sections.push(option.section)
        }
      }
    }
  }
  if (domainLogic.caveats && Object.keys(domainLogic.caveats).length > 0) {
    for (var id in domainLogic.caveats) {
      const option = options.find((o) => parseInt(o.id) === parseInt(id));
      if (option && !isEmpty(domainLogic.caveats[id])) {
        if(!sections.includes(option.section)){
          sections.push(option.section)
        }
      }
    }
  }
  //hard code special case questions dealing with CPUE where they are listed as normal to show up in the questionnaire
  //but no cavs/crits directly associated with the individual question. answers only used in satisfiedby questions
  if(question.id === 263 || question.id === 275){
    sections = ["assessment"];
  }

  return sections;
}

export const calculateResults = (questions, answers, options, categories, tags) => {
  // console.time("calc");
  answers = answers || {};
  let questionCompletionStates = new QuestionCompletionState();

  let categoriesLookup = categories.reduce((list, c) => {
    list[c.id] = { ...c };
    return list;
  }, {});
  let questionsLookup = questions.reduce((list, q) => {
    list[q.id] = { ...q };
    return list;
  }, {});
  let optionLookup = options.reduce((list, t) => {
    list[t.id] = {
      ...t,
      category: categoriesLookup[t.category],
      inputCategory: categoriesLookup[t.inputCategory],
      score: 0,
      caveats: [],
      eliminated: false,
      eliminatedBy: [],
      metCriteria: [],
      tags: (t.tags || []).map((id) => tags.find((t) => t.id === id))
    };
    return list;
  }, {});
  let questionInfluenceScores = {
    monitoring: questions.reduce((l, q) => {
      l[q.id] = 0;
      return l;
    }, {}),
    assessment: questions.reduce((l, q) => {
      l[q.id] = 0;
      return l;
    }, {}),
    controlRules: questions.reduce((l, q) => {
      l[q.id] = 0;
      return l;
    }, {}),
    enforcement: questions.reduce((l, q) => {
      l[q.id] = 0;
      return l;
    }, {})
  };
  let results = questions.reduce(
    (results, question) => {
      if (question.answerType !== "satisfiedBy") {
        let answer = answers[question.id];
        if (answer && question.answers.map((a) => a.id).indexOf(answer.value) === -1) {
          answer = null;
        }
        // caveats
        if (answer) {
          if (
            answer.flagged &&
            answer.answeredInSection &&
            !(question.id in results[answer.answeredInSection].flaggedQuestions)
          ) {
            let ans = question.answers.find(a => a.id === answer.value);
            //display bookmark for each section the question is asked in
            let sections = getQuestionSections(question, options);
            sections.forEach((section) => {
              results[section].flaggedQuestions[question.id] = {
                ...question,
                answerText: ans ? ans.title : null,
                answer: answer
              };
            })
          }
        }
        for (let optionID in question.domainLogic.caveats) {
          let option = optionLookup[optionID];
          if (option.id in question.domainLogic.caveats && Object.keys(question.domainLogic.caveats[option.id]).length > 0) {
            if (!answer) {
              // console.log(option.section, 'marking incomplete due to missing answer for satisfiedBy question', question.domainLogic.caveats[option.id], answer)
              questionCompletionStates.markIncomplete(
                option.section,
                question.id
              );
            } else {
              if (answer.answeredInSection === option.section) {
                questionCompletionStates.markCompleted(
                  option.section,
                  question.id
                );
              }
              if (answer.value in question.domainLogic.caveats[optionID]) {
                let caveat =
                  question.domainLogic.caveats[optionID][answer.value];
                if (caveat.title && caveat.title.length) {
                  optionLookup[optionID].score += caveat.score;
                  optionLookup[optionID].caveats.push({
                    score: caveat.score,
                    title: caveat.title,
                    question: question.id
                  });
                  questionInfluenceScores[optionLookup[optionID].section][
                    question.id
                  ] += influence(caveat);
                }
              }
            }
          }
        }
        // criteria
        for (let optionID in question.domainLogic.criteria) {
          if (
            optionID in question.domainLogic.criteria &&
            question.domainLogic.criteria[optionID] &&
            question.domainLogic.criteria[optionID].length
          ) {
            let option = optionLookup[optionID];
            if (answer) {
              questionCompletionStates.markCompleted(
                option.section,
                question.id
              );
              if (
                question.domainLogic.criteria[optionID].indexOf(
                  answer.value
                ) === -1
              ) {
                // doesn't meet criteria
                optionLookup[optionID].eliminated = true;
                optionLookup[optionID].eliminatedBy.push({
                  ...question,
                  answer: answer.value,
                  color: getCriteriaColor(answer.value, question.answers, question.domainLogic.criteria[optionID])
                });
                // CB 2/8/18 Criteria no longer effects influence score
                questionInfluenceScores[optionLookup[optionID].section][
                  question.id
                ] += 10000;
              } else {
                optionLookup[optionID].metCriteria.push({
                  ...question,
                  answer: answer.value,
                  color: getCriteriaColor(answer.value, question.answers, question.domainLogic.criteria[optionID])
                });
              }
            } else {
              questionCompletionStates.markIncomplete(
                option.section,
                question.id
              );
            }
          }
        }
      } else {
        // satisfiedBy
        // just handle criteria
        let questionIDs = question.satisfiedBy;
        for (let optionID in question.domainLogic.criteria) {
          const passingQuestions = [];
          const failedQuestions = [];
          for (let questionID of questionIDs) {
            const satisfyingQuestion = questionsLookup[questionID];
            if(typeof satisfyingQuestion !== 'undefined'){
              let val = answers[questionID] ? answers[questionID].value : null;
              let option = optionLookup[optionID];
              if (!val) {
                questionCompletionStates.markIncomplete(
                  option.section,
                  questionID
                );
              } else {
                // add to passed
                questionCompletionStates.markCompleted(
                option.section,
                questionID
                );
              }
              let criteria = question.domainLogic.criteria[optionID];
              if (criteria && criteria.length) {
                if (criteria.indexOf(val) === -1) {
                  // add to failed
                  // console.log('question', satisfyingQuestion);
                  failedQuestions.push({
                    ...satisfyingQuestion,
                    answer: val,
                    color: getCriteriaColor(val, satisfyingQuestion.answers, criteria),
                    // Somehow, we will bundle these together by setting an OR attribute to the original questionID
                    or: question.id
                  });
                } else {
                  // add to passed
                  passingQuestions.push({
                    ...satisfyingQuestion,
                    answer: val,
                    color: getCriteriaColor(val, satisfyingQuestion.answers, criteria),
                    or: question.id
                  });
                }
              }
            }
          }
          // If failed all questions, put all into eliminatedBy
          if (passingQuestions.length === 0 && failedQuestions.length > 0) {
            optionLookup[optionID].eliminated = true;
            optionLookup[optionID].eliminatedBy = optionLookup[optionID].eliminatedBy.concat(failedQuestions);
          } else {
            // If failed some but passed at least one, put into metCriteria
            optionLookup[optionID].metCriteria = optionLookup[optionID].metCriteria.concat([...passingQuestions, ...failedQuestions]);
          }
        }
      }
      return results;
    },
    ["monitoring", "assessment", "controlRules", "enforcement"].reduce((list, section) => {
      list[section] = {
        influentialAnswers: [],
        options: [],
        eliminatedOptions: [],
        flaggedQuestions: []
      };
      return list;
    }, {})
  );
  for (let key of Object.keys(optionLookup)) {
    let option = optionLookup[key];
    option.additionalCaveats.forEach(caveat => {
      option.score = option.score + influence(caveat);
      option.caveats.push({...caveat, static: true});
    });
    results[option.section].options.push(option);
  }
  results.scores = {};
  for (let section of ["monitoring", "assessment", "controlRules", "enforcement"]) {
    results[section] = {
      ...results[section],
      completedQuestions: questionCompletionStates.numCompletedQuestions(
        section
      ),
      completedQuestionsObjects: questionCompletionStates.completedQuestions,
      incompleteQuestionsObjects: questionCompletionStates.incompleteQuestions,
      incompleteQuestions: questionCompletionStates.numIncompleteQuestions(
        section
      ),
      complete: questionCompletionStates.isComplete(section),
      nextQuestion: nextQuestionForSection(
        section,
        { answers: answers },
        { questions, options }
      )
    };
    results[section].flaggedQuestions = Object.keys(
      results[section].flaggedQuestions
    ).map(k => results[section].flaggedQuestions[k]);

    let scores = questionInfluenceScores[section];
    if (results[section].incomplete) {
      results.scores = {};
    } else {
      var questionScores = Object.keys(scores).map(questionID => [
        questionID,
        scores[questionID]
      ]);
      questionScores.sort((a, b) => a[1] - b[1]).reverse();
      for (var score of questionScores.slice(0, 10)) {
        let question = questions.find(q => q.id.toString() === score[0]);
        let answer = answers[question.id];
        if (answer) {
          results[section].influentialAnswers.push({
            ...answers[question.id],
            optionsAffected: score[1],
            question: {
              ...question
            }
          });
        }
      }
      questionScores.reduce((l, data) => {
        if (l[data[0]]) {
          l[data[0]] += data[1];
        } else {
          l[data[0]] = data[1];
        }
        return l;
      }, results.scores);
    }
  }
  // if (results['controlRules'].options[6]) {
  //   console.log('results', results['controlRules'].options[6].caveats);
  // }

  // console.timeEnd("calc");
  return results;
};

const getResponseID = state => state.responses.responseID;
export const getResponses = state => state.responses.items || [];
export const getAllResponses = state => state.responses.allResponses || [];
const getSearchResponses = state => state.responses.searchItems || [];
export const getFilterOptions = state => state.filterOptions;

export const getResponse = createSelector(
  [getResponseID, getResponses, getAllResponses, getSearchResponses],
  (id, responses, allResponses, searchResponses) => {
    let response = responses.find(res => res.metadata.id === id);
    if (!response) {
      for (let key in allResponses.pages) {
        const page = allResponses.pages[key];
        for (let r of page.items) {
          if (r.metadata.id === id) {
            return r;
          }
        }
      }
    }
    if(!response) {
      for (let r of searchResponses) {
        if (r.metadata.id === id) {
          return r;
        }
      }
    }
    return response;
  }
);

const numGreen = (t) => t.caveats.filter((c) => c.score === 2).length

const totalScore = (o) => o.caveats.reduce(function(score, caveat) {
  var influence = 0
  if (caveat.score < 0) {
    switch (caveat.score) {
      case -3:
        influence = 100000;
        break;
      case -2:
        influence = 1000;
        break;
      case -1:
        influence = 10;
        break;
      default:
        influence = 0;
    }
  }
  return score + influence;
}, 0);

const getTags = state => state.tags;

export const getResults = createSelector(
  [getQuestions, getResponse, getOptions, getCategories, getFilterOptions, getTags],
  (questions, response, options, categories, filterOptions, tags) => {
    let answers = response ? response.answers : {};
    let results = calculateResults(questions, answers, options, categories, tags);
    for (var section of ['monitoring', 'assessment', 'controlRules', 'enforcement']) {
      const { order, displayHiddenOptions, limitToTags, hideTags, limitedTags, hiddenTags } = filterOptions[section];
      const userEdits = response ? (response.userEdits ? response.userEdits[section] || {} : {}) : {};
      const hiddenOptIDs = userEdits.hiddenOptIDs || [];
      const userSortOrder = userEdits.userSortOrder || [];
      let categoryProp = "category";
      if (section === 'assessment' && filterOptions && filterOptions[section] && filterOptions[section].categoryProp && /input/i.test(filterOptions[section].categoryProp)) {
        categoryProp = "inputCategory";
      }
      if (!displayHiddenOptions) {
        results[section].options = results[section].options.filter((o) => hiddenOptIDs.indexOf([o.id]) === -1)
      }
      if (order === DEFAULT_ORDER) {
        results[section].options.sort((a, b) => {
          //move eliminated options to the end
          if (a.eliminated && !b.eliminated) {
            return 1;
          } else if (b.eliminated && !a.eliminated) {
            return -1;
          }
          //sort by tiers for non eliminated(preAssessments and data-poor below data-mid/high)
          if(!a.eliminated && !b.eliminated){
            if((a.assessmentTier === "data-mid" || a.assessmentTier === "data-high") && !(b.assessmentTier === "data-mid" || b.assessmentTier === "data-high")){
              return -1;
            } else if ((b.assessmentTier === "data-mid" || b.assessmentTier === "data-high") && !(a.assessmentTier === "data-mid" || a.assessmentTier === "data-high")){
              return 1;
            }
          }

          //if both same eliminated status and tier group, then sort by score
          const scoreB = totalScore(b);
          const scoreA = totalScore(a);
          if (scoreB === scoreA) {
            return a.title.localeCompare(b.title);
          } else {
            return totalScore(b) - totalScore(a);
          }
        });
      } else if (order === ORDER_BY_NAME) {
        results[section].options.sort((a, b) => a.title.localeCompare(b.title));
      } else if (order === ORDER_BY_CATEGORY) {
        results[section].options.sort((a, b) => {
          const titleA = a[categoryProp] ? a[categoryProp].title : "";
          const titleB = b[categoryProp] ? b[categoryProp].title : "";
          return titleA.localeCompare(titleB)
        });
      } else if (order === ORDER_BY_POSITIVE_ATTRIBUTES) {
        results[section].options.sort((a, b) => numGreen(b) - numGreen(a));
      } else if (order === CUSTOMIZED_ORDER) {
        results[section].options = results[section].options.sort((a, b) => userSortOrder.indexOf(a.id) - userSortOrder.indexOf(b.id))
      } else if (order === ORDER_BY_ASSESSMENT_TIER) {
        results[section].options = results[section].options.sort((a, b) => assessmentTiers.find(tier => tier.id === a.assessmentTier).sortOrder - assessmentTiers.find(tier => tier.id === b.assessmentTier).sortOrder)
      }

      // hidden options
      if (!displayHiddenOptions) {
        results[section].options = results[section].options.filter((option) => hiddenOptIDs.indexOf(option.id) === -1)
      }

      if (limitToTags) {
        if (limitedTags && limitedTags.length > 0) {
          results[section].options = results[section].options.filter((option) => {
            if (!option.tags || option.tags.length === 0) {
              return false;
            } else {
              var allFalse = true;
              for (const tagID of limitedTags) {
                if (option.tags.indexOf(tagID) !== -1) {
                  allFalse = false;
                }
              }
              return !allFalse;
            }
          });
        }
      } else if (hideTags && hiddenTags.length) {
        results[section].options = results[section].options.filter((option) => {
          if (!option.tags || option.tags.length === 0) {
            return true;
          } else {
            for (var tagID of hiddenTags) {
              if (option.tags.indexOf(tagID) !== -1) {
                return false;
              }
            }
            return true;
          }
        });
      }
    }

    return results;
  }
);

export const answerTextLookup = createSelector(
  [getQuestions, getResponse],
  (questions, response) => {
    let answers = response ? response.answers : {};
    let lookup = questions.reduce((list, question) => {
      let answer = answers[question.id];
      if (answer) {
        let fullAnswer = question.answers.find(a => a.id === answer.value);
        if (fullAnswer) {
          list[question.id] = fullAnswer.title;
        }
      }
      return list;
    }, {});
    return lookup;
  }
);

export const effectOnOptions = (
  state,
  questionID,
  readOnly,
  response,
  optionalAnswer,
  section
) => {
  let question = state.questions.find(q => q.id === questionID);
  if (!question) {
    return {
      numOptions: 0,
      options: []
    };
  }
  let answer =
    optionalAnswer ||
    (response.answers[question.id]
      ? response.answers[question.id].value
      : null);
  if (!answer) {
    return {
      numOptions: 0,
      options: []
    };
  }
  let criteria = question.domainLogic.criteria;
  let caveats = question.domainLogic.caveats;
  let options = [];
  Object.keys(criteria).filter((k) => criteria[k] && criteria[k].length).forEach(optionID => {
    if ((criteria[optionID] || []).indexOf(answer) === -1) {
      let option = state.options.find(t => t.id.toString() === optionID);
      if (!section || option.section === section) {
        if (!option) {
          throw new Error("Cant find option" + optionID);
        } else {
          options.push({
            ...option,
            eliminated: true
          });
        }
      }
    }
  });

  Object.keys(caveats).forEach(optionID => {
    let caveat = caveats[optionID][answer];
    if (caveat) {
      let option = state.options.find(t => t.id.toString() === optionID);
      if (!section || option.section === section) {
        options.push({
          ...option,
          caveat: caveat
        });
      }
    }
  });

  const score = option => (option.caveat ? option.caveat.score : -1);

  return {
    numOptions: options.length,
    options: options.sort((a, b) => score(a) - score(b))
  };
};

export const domainLogicToCounts = (question, answer, options, section) => {
  var redCaveats = 0;
  var orangeCaveats = 0;
  var yellowCaveats = 0;
  //var greenCaveats = 0;
  var metCriteria = 0;
  var unmetCriteria = 0;

  let lookup = false;
  if (options && options.length && section) {
    lookup = {};
    for (let option of options) {
      if (option.section === section) {
        lookup[option.id] = true;
      }
    }
  }

  const inScope = (optionID) => {
    if (lookup) {
      return !!lookup[optionID];
    } else {
      return true;
    }
  }

  for (const optionID in question.domainLogic.caveats) {
    if (inScope(optionID)) {
      if (question.domainLogic.caveats[optionID][answer]) {
        switch (question.domainLogic.caveats[optionID][answer].score) {
          case -3:
            redCaveats++;
            break;
          case -2:
            orangeCaveats++;
            break;
          case -1:
            yellowCaveats++;
            break;
          default:
          // do nothing
        }
      }
    }
  }

  for (const optionID in question.domainLogic.criteria) {
    if (
      question.domainLogic.criteria[optionID] &&
      question.domainLogic.criteria[optionID].length
    ) {
      if (question.domainLogic.criteria[optionID].indexOf(answer) !== -1) {
        metCriteria++;
      } else {
        unmetCriteria++;
      }
    }
  }

  return {
    redCaveats: redCaveats,
    orangeCaveats: orangeCaveats,
    yellowCaveats: yellowCaveats,
    metCriteria: metCriteria,
    unmetCriteria: unmetCriteria
  };
};
